import { vec2, type Vec2 } from '../math/vec2.ts';

export type Polarity = 1 | -1;
export type MagnetSize = 'S' | 'M' | 'L';

/** Relative strength multipliers. The prototype ships with M only (see Game). */
export const MAGNET_STRENGTHS: Record<MagnetSize, number> = {
  S: 0.6,
  M: 1.0,
  L: 1.7,
};

/** Body radius in world units — also the hit target for dragging. */
export const MAGNET_RADII: Record<MagnetSize, number> = {
  S: 34,
  M: 42,
  L: 52,
};

let nextId = 0;

export class Magnet {
  readonly id: string;
  readonly position: Vec2;
  polarity: Polarity;
  size: MagnetSize;
  strength: number;
  radius: number;

  /**
   * Presentation state, 0..1: whether this magnet has enough fluid inside its critical
   * radius to have thrown up a spike crown. Rises fast and falls slowly, which is what
   * gives the Rosensweig instability its hysteresis. Written by the fluid renderer;
   * nothing in the simulation reads it.
   */
  spikeIgnition = 0;

  constructor(x: number, y: number, polarity: Polarity = 1, size: MagnetSize = 'M') {
    this.id = `m${nextId++}`;
    this.position = vec2(x, y);
    this.polarity = polarity;
    this.size = size;
    this.strength = MAGNET_STRENGTHS[size];
    this.radius = MAGNET_RADII[size];
  }

  setSize(size: MagnetSize): void {
    this.size = size;
    this.strength = MAGNET_STRENGTHS[size];
    this.radius = MAGNET_RADII[size];
  }

  flip(): void {
    this.polarity = this.polarity === 1 ? -1 : 1;
  }

  /** Signed strength — the only quantity the field maths actually needs. */
  get signedStrength(): number {
    return this.polarity * this.strength;
  }

  /** Generous touch radius: fingers are bigger than magnets. */
  hitRadius(): number {
    return this.radius + 26;
  }
}
